import {useLayoutEffect} from "react";
import {useLanguage} from "../screens/Authentication/LanguageContext";
import {siteTranslations, type SiteLocale} from "./siteTranslations";

/**
 * Localize text-only website chrome without modifying the app's access checks,
 * book reader, form values, links or API responses. The original German text
 * remains available for switching languages. No external translation requests.
 *
 * Static React copy and dynamic labels (including modals opened later) are
 * handled after DOM commits; observing children makes the text-only transform
 * work across independently maintained existing page components.
 */
const originalText = new WeakMap<Text, {source: string; lastOutput: string}>();
const originalAttributes = new WeakMap<Element, Record<string, {source: string; lastOutput: string}>>();
const attributes = ["title", "aria-label", "placeholder", "alt"] as const;
const skipSelector = [
  "script", "style", "noscript", "code", "pre", "textarea", "input",
  "svg", "[contenteditable]", "[data-site-no-translate]",
  ".dl-native-language", ".daily-word-card", ".daily-word-content", ".daily-word-panel",
  ".book-document-frame", ".book-view-frame", "iframe",
].join(",");

const normalize = (value: string) => value.replace(/\s+/g, " ").trim().normalize("NFC");

const translate = (value: string, language: SiteLocale): string | null => {
  const normalized = normalize(value);
  if (!normalized) return null;
  const entry = siteTranslations[normalized];
  if (entry && entry[language]) return entry[language];
  // Footer has a dynamic year, but only its rights notice is translatable.
  if (normalized === "Dear Language. Alle Rechte vorbehalten.") {
    const rights = siteTranslations["Alle Rechte vorbehalten."]?.[language];
    if (rights) return `Dear Language. ${rights}`;
  }
  return null;
};

const preserveWhitespace = (source: string, translated: string): string => {
  const leading = source.match(/^\s*/)?.[0] || "";
  const trailing = source.match(/\s*$/)?.[0] || "";
  return `${leading}${translated}${trailing}`;
};

/**
 * Content containers show the German original with a second, translated line.
 * Keep the compact header/footer navigation single-language. No DOM children
 * are inserted: CSS paints the translation from an attribute, so React retains
 * full ownership of its nodes and click handlers.
 */
const contentContainerSelector = [
  ".mainRouteContent",
  ".login-modal-card", ".subscription-monthly-card", ".unsubscribe-card",
  ".course-payment-card", ".book-alert-popup",
].join(",");
const bilingualTextSelector = "h1,h2,h3,h4,p,span,strong,small,summary,label,li,div";

function canShowTwoLines(owner: Element): boolean {
  if (!owner.closest(contentContainerSelector) || !owner.matches(bilingualTextSelector)) return false;
  if (owner.closest(".daily-word-card,.dl-bilingual-daily,.book-document-frame,.book-view-frame,[data-site-no-translate]")) return false;
  // Never duplicate values such as names, emails and numeric amounts; those are
  // unrecognised in the fixed translations catalogue and pass through intact.
  // A single direct text segment also prevents ambiguous pseudo-elements on
  // React structures that contain several independently translated fragments.
  return Array.from(owner.childNodes).filter(child => child.nodeType === 3 && Boolean(child.textContent?.trim())).length === 1;
}

function localizeTextNode(node: Text, language: SiteLocale) {
  const current = node.nodeValue || "";
  const owner = node.parentElement;
  if (!owner || owner.closest(skipSelector)) return;
  let entry = originalText.get(node);
  if (!entry || (current !== entry.lastOutput && current !== entry.source)) {
    entry = {source: current, lastOutput: current};
  }
  const translated = translate(entry.source, language);
  const showTwo = canShowTwoLines(owner) && Boolean(translated) &&
    normalize(translated || "") !== normalize(entry.source);
  if (showTwo) {
    // Restore German if a previous language pass had translated this text.
    // React-owned children stay intact; translations are decorative CSS text.
    if (owner.getAttribute("data-dl-translation") !== translated) {
      owner.setAttribute("data-dl-translation", translated as string);
    }
    owner.setAttribute("data-dl-translation-lang", language);
    if (current !== entry.source) node.nodeValue = entry.source;
    entry.lastOutput = entry.source;
  } else {
    if (owner.hasAttribute("data-dl-translation")) {
      owner.removeAttribute("data-dl-translation");
      owner.removeAttribute("data-dl-translation-lang");
    }
    const next = translated === null ? entry.source : preserveWhitespace(entry.source, translated);
    if (current !== next) node.nodeValue = next;
    entry.lastOutput = next;
  }
  originalText.set(node, entry);
}

function localizeAttributes(element: Element, language: SiteLocale) {
  if (element.closest(skipSelector)) return;
  let state = originalAttributes.get(element) || {};
  for (const attribute of attributes) {
    const current = element.getAttribute(attribute);
    if (current === null) continue;
    let entry = state[attribute];
    if (!entry || (current !== entry.lastOutput && current !== entry.source)) {
      entry = {source: current, lastOutput: current};
    }
    const next = translate(entry.source, language) || entry.source;
    entry.lastOutput = next;
    state[attribute] = entry;
    if (next !== current) element.setAttribute(attribute, next);
  }
  originalAttributes.set(element, state);
}

function localizeSubtree(root: Element, language: SiteLocale) {
  if (root.matches(skipSelector)) return;
  localizeAttributes(root, language);
  const elements = root.querySelectorAll("[title],[aria-label],[placeholder],[alt]");
  elements.forEach(element => localizeAttributes(element, language));
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current: Node | null;
  while ((current = walker.nextNode())) localizeTextNode(current as Text, language);
}

export default function SiteLanguageBridge() {
  const {language} = useLanguage();
  useLayoutEffect(() => {
    const locale: SiteLocale = language === "de" ? "en" : language;
    document.documentElement.lang = locale === "zh" ? "zh-CN" : locale === "ta" ? "ta" : locale;
    document.documentElement.dir = "ltr"; // Preserve existing left-aligned Arabic selector and layouts.
    document.documentElement.dataset.siteLanguage = locale;
    const root = document.getElementById("root");
    if (!root) return;
    let requested = false;
    let frame = 0;
    const refresh = () => {
      requested = false;
      localizeSubtree(root, locale);
    };
    // Synchronous initial application after React commits, then batch changes.
    refresh();
    const observer = new MutationObserver(() => {
      if (requested) return;
      requested = true;
      frame = window.requestAnimationFrame(refresh);
    });
    observer.observe(root, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...attributes]});
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [language]);
  return null;
}
